import { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Modal,
  Dropdown,
  InputGroup,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSignOut } from "react-auth-kit";
import { NavBar } from "../navbar/navbar";
import { useApi } from "../home/apiService";
import { useLanguage } from "../../i18n/LanguageProvider";
import { usePermissions } from "../../hooks/usePermissions";
import { ApiDocsModal } from "./ApiDocsModal";
import "../../styles/sp-ui.css";
import "./Account.css";

interface AccountData {
  id: string;
  email: string;
  username?: string;
}

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  totalRequests: number;
  lastUsedAt: string | null;
  isActive: boolean;
  createdAt: string;
}

interface PlanInfo {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
  stripePriceId: string | null;
}

const KebabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
  </svg>
);

export const Account = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const signOut = useSignOut();
  const { t } = useLanguage();
  const { hasPermission } = usePermissions();
  const canManageApiKeys = hasPermission("api_keys", "manage");

  // Data state
  const [account, setAccount] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);

  // Username edit
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);

  // Delete account
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // API keys
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [showDeleteKeyModal, setShowDeleteKeyModal] = useState(false);
  const [deleteKeyTarget, setDeleteKeyTarget] = useState<ApiKey | null>(null);
  const [deletingKey, setDeletingKey] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);

  // Plan & upgrade
  const [currentPlan, setCurrentPlan] = useState<PlanInfo | null>(null);
  const [selectablePlans, setSelectablePlans] = useState<PlanInfo[]>([]);
  const [pendingRequest, setPendingRequest] = useState<{ id: string; requestedPlanId: string; requestedAt: string } | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [hasBilling, setHasBilling] = useState(false);
  // Plan granted outside Stripe (admin-assigned or hidden): the server hides
  // paid plans from `selectable` and /checkout refuses such accounts, so the
  // UI only explains why there is nothing to buy.
  const [planLocked, setPlanLocked] = useState(false);
  // Pending-cancellation state, live-read from Stripe (not stored locally).
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePlanId, setSelectedUpgradePlanId] = useState<string>("");
  const [submittingUpgrade, setSubmittingUpgrade] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [managingBilling, setManagingBilling] = useState(false);

  useEffect(() => {
    const fetchAccountData = async () => {
      setLoading(true);
      try {
        const accountResponse = await api.fetchCurrentAccount();
        const planResponse = await api.fetchMyPlan().catch(() => null);

        if (planResponse) {
          setCurrentPlan(planResponse.current);
          setSelectablePlans(planResponse.selectable);
          setPendingRequest(planResponse.pendingRequest);
          setSubscriptionStatus(planResponse.subscriptionStatus);
          setHasBilling(planResponse.hasBilling);
          setPlanLocked(planResponse.planLocked ?? false);
          if (
            planResponse.hasBilling &&
            ["active", "trialing", "past_due"].includes(planResponse.subscriptionStatus ?? "")
          ) {
            // Non-blocking: the rest of the page renders while Stripe answers.
            api
              .fetchBillingSubscription()
              .then((sub) => {
                setCancelAtPeriodEnd(sub.cancelAtPeriodEnd);
                setCurrentPeriodEnd(sub.currentPeriodEnd);
              })
              .catch(() => {});
          }
        }

        setAccount(accountResponse.account);
        if (canManageApiKeys) {
          api
            .fetchMyApiKeys()
            .then((r) => setApiKeys(r.apiKeys || []))
            .catch(() => {
              console.error("Failed to fetch API keys");
            });
        }
        setEditUsername(accountResponse.account?.username || "");
      } catch (error) {
        console.error("Error fetching account data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveUsername = async () => {
    if (!account || !editUsername.trim()) return;
    setSavingUsername(true);
    try {
      await api.updateAccount({ username: editUsername.trim() });
      setAccount({ ...account, username: editUsername.trim() });
      setIsEditingUsername(false);
    } catch (error) {
      console.error("Error updating username:", error);
    } finally {
      setSavingUsername(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.deleteMyAccount();
      setShowDeleteModal(false);
      signOut();
      navigate("/login");
    } catch (error: any) {
      console.error("Error deleting account:", error);
      alert(error?.message || "Failed to delete account.");
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenUpgrade = () => {
    setUpgradeError(null);
    setSelectedUpgradePlanId(selectablePlans[0]?.id || "");
    setShowUpgradeModal(true);
  };

  const selectedPlan = selectablePlans.find((p) => p.id === selectedUpgradePlanId) || null;
  const selectedIsPaid = !!selectedPlan?.stripePriceId;
  const hasActiveStripeSub =
    hasBilling && ["active", "trialing", "past_due"].includes(subscriptionStatus ?? "");
  const showCancellationNotice =
    cancelAtPeriodEnd && ["active", "trialing"].includes(subscriptionStatus ?? "");

  // Deep link from the navbar CTA: /account?upgrade=1 auto-opens the plan
  // modal once plan data is in. The param is stripped (replace) either way so
  // refresh/back won't reopen it, and paid users just land on the page.
  useEffect(() => {
    if (loading || !searchParams.get("upgrade")) return;
    if (selectablePlans.length > 0 && !pendingRequest && !showCancellationNotice) {
      handleOpenUpgrade();
    }
    const next = new URLSearchParams(searchParams);
    next.delete("upgrade");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleSubmitUpgrade = async () => {
    if (!selectedUpgradePlanId) return;
    setSubmittingUpgrade(true);
    setUpgradeError(null);
    try {
      if (selectedIsPaid && hasActiveStripeSub) {
        // Paid -> paid switch: hand off to the portal's confirm screen, which
        // shows the prorated amount and applies the change immediately. The
        // plan flips via webhook, same as checkout.
        const { url } = await api.createPortalSession(
          "subscription_update_confirm",
          selectedUpgradePlanId,
        );
        window.location.href = url;
        return;
      }
      if (selectedIsPaid) {
        // Paid plan: hand off to Stripe Checkout. The plan flips via webhook once
        // payment succeeds; Stripe redirects back to /account/billing.
        const { url } = await api.createCheckoutSession(selectedUpgradePlanId);
        window.location.href = url;
        return;
      }
      if (hasActiveStripeSub) {
        // Downgrade to free with a live subscription = cancel it in Stripe.
        // The portal's cancel flow sets cancel-at-period-end; the webhook
        // downgrades the plan when the period actually ends.
        const { url } = await api.createPortalSession("subscription_cancel");
        window.location.href = url;
        return;
      }
      // Free / admin-assigned plan: keep the manual admin-approval flow.
      const response = await api.requestPlanChange(selectedUpgradePlanId);
      setPendingRequest({
        id: response.id,
        requestedPlanId: selectedUpgradePlanId,
        requestedAt: response.requestedAt,
      });
      setShowUpgradeModal(false);
    } catch (error: any) {
      setUpgradeError(error?.message || "Kunde inte skicka uppgraderingsförfrågan.");
    } finally {
      setSubmittingUpgrade(false);
    }
  };

  const handleManageBilling = async () => {
    setManagingBilling(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (error) {
      console.error("Error opening billing portal:", error);
      alert("Kunde inte öppna prenumerationsportalen. Försök igen.");
      setManagingBilling(false);
    }
  };

  const handleCreateKey = () => {
    setNewKeyName("");
    setCreatedKey(null);
    setKeyCopied(false);
    setShowCreateKeyModal(true);
  };

  const handleSubmitCreateKey = async () => {
    if (!newKeyName.trim()) return;
    setSavingKey(true);
    try {
      const response = await api.createMyApiKey(newKeyName.trim());
      setApiKeys((prev) => [response.apiKey, ...prev]);
      setCreatedKey(response.key);
    } catch (error) {
      console.error("Error creating API key:", error);
      alert("Kunde inte skapa API-nyckeln. Försök igen.");
    } finally {
      setSavingKey(false);
    }
  };

  const handleToggleKey = async (apiKey: ApiKey) => {
    try {
      const response = await api.toggleMyApiKey(apiKey.id, !apiKey.isActive);
      setApiKeys((prev) => prev.map((k) => (k.id === apiKey.id ? response.apiKey : k)));
    } catch (error) {
      console.error("Error toggling API key:", error);
      alert("Kunde inte uppdatera API-nyckeln. Försök igen.");
    }
  };

  const handleDeleteKey = (apiKey: ApiKey) => {
    setDeleteKeyTarget(apiKey);
    setShowDeleteKeyModal(true);
  };

  const handleConfirmDeleteKey = async () => {
    if (!deleteKeyTarget) return;
    setDeletingKey(true);
    try {
      await api.deleteMyApiKey(deleteKeyTarget.id);
      setApiKeys((prev) => prev.filter((k) => k.id !== deleteKeyTarget.id));
      setShowDeleteKeyModal(false);
      setDeleteKeyTarget(null);
    } catch (error) {
      console.error("Error deleting API key:", error);
      alert("Kunde inte ta bort API-nyckeln. Försök igen.");
    } finally {
      setDeletingKey(false);
    }
  };

  const handleCopyKey = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey);
    setKeyCopied(true);
  };

  if (loading) {
    return (
      <>
        <NavBar />
        <Container fluid className="sp-page">
          <div className="sp-empty">
            <span className="sp-spinner" />
          </div>
        </Container>
      </>
    );
  }

  if (!account) {
    return (
      <>
        <NavBar />
        <Container fluid className="sp-page">
          <div className="sp-empty">{t("account.notFound")}</div>
        </Container>
      </>
    );
  }

  const displayName = account.username || account.email.split("@")[0];
  const initial = (displayName[0] || "?").toUpperCase();

  return (
    <>
      <NavBar />
      <Container fluid className="sp-page">
        <div className="sp-page__head">
          <h1 className="sp-page__title">{t("account.title")}</h1>
          <p className="sp-page__sub">{t("account.subtitle")}</p>
        </div>

        <Row className="g-4">
          {/* Profile column */}
          <Col lg={4}>
            <div className="sp-card">
              <div className="sp-card__head">
                <h3 className="sp-card__title">{t("account.settings")}</h3>
              </div>

              {/* Identity */}
              <div className="account-identity">
                <span className="sp-avatar sp-avatar--lg">{initial}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="account-identity__name">{displayName}</div>
                  <div className="account-identity__email">{account.email}</div>
                </div>
              </div>

              {/* Username field */}
              <div className="account-field">
                <label className="account-field__label">{t("account.username")}</label>
                {isEditingUsername ? (
                  <>
                    <input
                      type="text"
                      className="sp-input"
                      value={editUsername}
                      onChange={(e) => setEditUsername(e.target.value)}
                      placeholder={t("account.usernamePlaceholder")}
                      autoFocus
                      style={{ width: "100%" }}
                    />
                    <div className="account-field__actions">
                      <button
                        className="sp-btn sp-btn--primary sp-btn--sm"
                        onClick={handleSaveUsername}
                        disabled={savingUsername}
                      >
                        {savingUsername ? (
                          <span className="sp-spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />
                        ) : (
                          t("account.save")
                        )}
                      </button>
                      <button
                        className="sp-btn sp-btn--ghost sp-btn--sm"
                        onClick={() => {
                          setIsEditingUsername(false);
                          setEditUsername(account.username || "");
                        }}
                      >
                        {t("account.cancel")}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="account-field__display">
                    <span>{displayName}</span>
                    <button
                      className="sp-btn sp-btn--ghost sp-btn--sm"
                      onClick={() => setIsEditingUsername(true)}
                    >
                      {t("account.edit")}
                    </button>
                  </div>
                )}
              </div>

              {/* Email (read-only) */}
              <div className="account-field">
                <label className="account-field__label">{t("account.email")}</label>
                <div className="account-field__display">
                  <span>{account.email}</span>
                </div>
              </div>

              {/* Plan */}
              <div className="account-field">
                <label className="account-field__label">{t("account.planLabel")}</label>
                <div className="account-plan">
                  <div className="account-plan__current">
                    <span className="sp-chip sp-chip--accent account-plan__name">
                      {currentPlan?.name || t("account.planNone")}
                    </span>
                    {currentPlan?.description && (
                      <span className="account-plan__desc">{currentPlan.description}</span>
                    )}
                  </div>
                  {planLocked && (
                    <div className="account-plan__desc" style={{ marginTop: 8 }}>
                      {t("account.planManagedManually")}
                    </div>
                  )}
                  {showCancellationNotice && (
                    <div className="sp-alert sp-alert--info" style={{ marginTop: 8 }}>
                      {t("account.planCancellationPending", {
                        date: currentPeriodEnd
                          ? new Date(currentPeriodEnd).toLocaleDateString("sv-SE")
                          : "",
                      })}
                    </div>
                  )}
                  {showCancellationNotice ? (
                    <button
                      className="sp-btn sp-btn--ghost sp-btn--sm"
                      onClick={handleManageBilling}
                      disabled={managingBilling}
                      style={{ marginTop: 8 }}
                    >
                      {managingBilling ? "..." : t("account.planResumeSubscription")}
                    </button>
                  ) : pendingRequest ? (
                    <div className="sp-alert sp-alert--info" style={{ marginTop: 8 }}>
                      {t("account.planPendingRequest")}
                    </div>
                  ) : selectablePlans.length > 0 ? (
                    <button
                      className={`sp-btn sp-btn--primary${currentPlan?.stripePriceId ? " sp-btn--sm" : ""}`}
                      onClick={handleOpenUpgrade}
                      style={{ marginTop: 8 }}
                    >
                      {currentPlan?.stripePriceId
                        ? t("account.planChange")
                        : t("account.planUpgrade")}
                    </button>
                  ) : !planLocked ? (
                    <div className="account-plan__desc" style={{ marginTop: 8 }}>
                      {t("account.planTopTier")}
                    </div>
                  ) : null}
                  {hasActiveStripeSub && !showCancellationNotice && (
                    <button
                      className="sp-btn sp-btn--ghost sp-btn--sm"
                      onClick={handleManageBilling}
                      disabled={managingBilling}
                      style={{ marginTop: 8 }}
                    >
                      {managingBilling ? "..." : t("account.manageSubscription")}
                    </button>
                  )}
                </div>
              </div>

              {/* Delete account */}
              <div className="account-danger">
                <button
                  className="sp-btn sp-btn--danger sp-btn--sm"
                  onClick={() => setShowDeleteModal(true)}
                >
                  {t("account.deleteAccount")}
                </button>
              </div>
            </div>
          </Col>

          {/* API keys column */}
          <Col lg={8}>
            {canManageApiKeys && (
              <div className="sp-card">
                <div className="sp-card__head">
                  <h3 className="sp-card__title">API-nycklar</h3>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      className="sp-btn sp-btn--ghost sp-btn--sm"
                      onClick={() => setShowDocsModal(true)}
                    >
                      {t("account.apiDocsButton") || "Docs"}
                    </button>
                    <button className="sp-btn sp-btn--primary sp-btn--sm" onClick={handleCreateKey}>
                      + Skapa
                    </button>
                  </div>
                </div>

                {apiKeys.length === 0 ? (
                  <div className="sp-empty">Inga nycklar skapade ännu.</div>
                ) : (
                  <div className="account-keys">
                    {apiKeys.map((apiKey) => (
                      <div key={apiKey.id} className="account-key">
                        <div className="account-key__info">
                          <div className="account-key__name">{apiKey.name}</div>
                          <div className="account-key__meta">
                            <code>{apiKey.keyPrefix}…</code>
                            <span>·</span>
                            <span>{apiKey.totalRequests.toLocaleString()} anrop</span>
                            {apiKey.lastUsedAt && (
                              <>
                                <span>·</span>
                                <span>
                                  Senast {new Date(apiKey.lastUsedAt).toLocaleDateString()}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <span
                          className={`sp-chip ${apiKey.isActive ? "sp-chip--success" : ""}`}
                        >
                          {apiKey.isActive ? "Aktiv" : "Inaktiv"}
                        </span>
                        <Dropdown align="end">
                          <Dropdown.Toggle as="button" className="sp-icon-btn" aria-label="Hantera">
                            <KebabIcon />
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            renderOnMount
                            popperConfig={{ strategy: "fixed" }}
                          >
                            <Dropdown.Item onClick={() => handleToggleKey(apiKey)}>
                              {apiKey.isActive ? "Inaktivera" : "Aktivera"}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() => handleDeleteKey(apiKey)}
                            >
                              Ta bort
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>

        {/* Delete account modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t("account.deleteAccount")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t("account.confirmDeleteAccount")}</p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="sp-btn sp-btn--ghost"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              {t("account.cancel")}
            </button>
            <button
              className="sp-btn sp-btn--danger"
              onClick={handleDeleteAccount}
              disabled={deleting}
            >
              {deleting ? "..." : t("account.deleteAccount")}
            </button>
          </Modal.Footer>
        </Modal>

        {/* Create API key modal */}
        <Modal
          show={showCreateKeyModal}
          onHide={() => !createdKey && setShowCreateKeyModal(false)}
          centered
          backdrop={createdKey ? "static" : true}
        >
          <Modal.Header closeButton={!createdKey}>
            <Modal.Title>{createdKey ? "API-nyckel skapad" : "Skapa API-nyckel"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!createdKey ? (
              <Form.Group>
                <Form.Label>Applikationsnamn</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="t.ex. Min integration"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  maxLength={255}
                  autoFocus
                />
              </Form.Group>
            ) : (
              <>
                <div className="sp-alert sp-alert--warn" style={{ marginBottom: 12 }}>
                  Denna nyckel visas bara en gång. Kopiera den nu.
                </div>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={createdKey}
                    readOnly
                    style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                  />
                  <button
                    className={`sp-btn ${keyCopied ? "sp-btn--primary" : ""}`}
                    onClick={handleCopyKey}
                  >
                    {keyCopied ? "Kopierad!" : "Kopiera"}
                  </button>
                </InputGroup>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {!createdKey ? (
              <>
                <button
                  className="sp-btn sp-btn--ghost"
                  onClick={() => setShowCreateKeyModal(false)}
                >
                  Avbryt
                </button>
                <button
                  className="sp-btn sp-btn--primary"
                  onClick={handleSubmitCreateKey}
                  disabled={savingKey || !newKeyName.trim()}
                >
                  {savingKey ? "Skapar..." : "Skapa"}
                </button>
              </>
            ) : (
              <button
                className="sp-btn sp-btn--primary"
                onClick={() => setShowCreateKeyModal(false)}
              >
                Klar
              </button>
            )}
          </Modal.Footer>
        </Modal>

        {/* Delete API key modal */}
        <Modal show={showDeleteKeyModal} onHide={() => setShowDeleteKeyModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Ta bort API-nyckel</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Är du säker på att du vill ta bort API-nyckeln{" "}
              <strong>{deleteKeyTarget?.name}</strong> permanent?
            </p>
            <p className="text-muted">
              Alla applikationer som använder denna nyckel förlorar åtkomst omedelbart.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="sp-btn sp-btn--ghost"
              onClick={() => setShowDeleteKeyModal(false)}
            >
              Avbryt
            </button>
            <button
              className="sp-btn sp-btn--danger"
              onClick={handleConfirmDeleteKey}
              disabled={deletingKey}
            >
              {deletingKey ? "Tar bort..." : "Ta bort"}
            </button>
          </Modal.Footer>
        </Modal>

        <ApiDocsModal show={showDocsModal} onHide={() => setShowDocsModal(false)} />

        {/* Plan upgrade modal */}
        <Modal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t("account.planChange")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ marginBottom: 12 }}>{t("account.planChangeIntro")}</p>
            {upgradeError && (
              <div className="sp-alert sp-alert--danger" style={{ marginBottom: 12 }}>
                {upgradeError}
              </div>
            )}
            <div className="upgrade-plan-list">
              {selectablePlans.map((p) => (
                <label key={p.id} className="upgrade-plan-option">
                  <input
                    type="radio"
                    name="upgrade-plan"
                    value={p.id}
                    checked={selectedUpgradePlanId === p.id}
                    onChange={() => setSelectedUpgradePlanId(p.id)}
                  />
                  <div className="upgrade-plan-option__body">
                    <div className="upgrade-plan-option__name">{p.name}</div>
                    {p.description && (
                      <div className="upgrade-plan-option__desc">{p.description}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="sp-alert sp-alert--info" style={{ marginTop: 12 }}>
              {selectedIsPaid && hasActiveStripeSub ? (
                <>
                  {t("account.planSwitchNote")}{" "}
                  {t("account.planTermsPrefix")}{" "}
                  <a
                    href="https://spotprisprognos.se/kopvillkor"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("account.planTermsLink")}
                  </a>
                  .
                </>
              ) : selectedIsPaid ? (
                <>
                  {t("account.planCheckoutNote")}{" "}
                  {t("account.planTermsPrefix")}{" "}
                  <a
                    href="https://spotprisprognos.se/kopvillkor"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("account.planTermsLink")}
                  </a>
                  .
                </>
              ) : hasActiveStripeSub ? (
                t("account.planCancelNote")
              ) : (
                t("account.planChangeApprovalNote")
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="sp-btn sp-btn--ghost"
              onClick={() => setShowUpgradeModal(false)}
              disabled={submittingUpgrade}
            >
              {t("account.cancel")}
            </button>
            <button
              className="sp-btn sp-btn--primary"
              onClick={handleSubmitUpgrade}
              disabled={submittingUpgrade || !selectedUpgradePlanId}
            >
              {submittingUpgrade
                ? "..."
                : selectedIsPaid && hasActiveStripeSub
                  ? t("account.planContinueToSwitch")
                  : selectedIsPaid
                    ? t("account.planContinueToPayment")
                    : hasActiveStripeSub
                      ? t("account.planContinueToCancel")
                      : t("account.planRequestUpgrade")}
            </button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};
